import clsx from "clsx";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Button from "components/common/Button";
import axios from "axios";
import toast from "react-hot-toast";
import { updateAnswers } from "store/auth/authSlice";
import isEmpty from "utils/is-empty";
import { REACT_APP_BACKEND_URI } from "config";

const QuizQuestion = () => {
  const { answers, role, submissionTimestamps } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [revealedAnswer, setRevealedAnswer] = useState("");
  const [questionsList, setQuestionsList] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentQuestionNum, setCurrentQuestionNum] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showPrevious, setShowPrevious] = useState(false);
  const [previousQuestions, setPreviousQuestions] = useState([]);

  
  // Calculate time remaining until 24 hours after submission using backend timestamp
  useEffect(() => {
    if (!currentQuestion?._id || answers[currentQuestion._id] === undefined) return;

    const calculateTimeRemaining = () => {
      const submissionTime = submissionTimestamps?.[currentQuestion._id];
      if (!submissionTime) {
        return 24 * 60 * 60 * 1000; // 24 hours in ms
      }

      const elapsed = Date.now() - parseInt(submissionTime);
      const remaining = (24 * 60 * 60 * 1000) - elapsed;
      return remaining > 0 ? remaining : 0;
    };

    setTimeRemaining(calculateTimeRemaining());

    const interval = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining());
    }, 1000);

    return () => clearInterval(interval);
  }, [currentQuestion, answers, submissionTimestamps]);

  const formatTime = (ms) => {
    if (ms <= 0) return "00:00:00";
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const prevClickHandler = () => {
    setCurrentQuestionNum((prev) => {
      if (prev === 0) return prev;
      else return prev - 1;
    });
  };

  const nextClickHandler = () => {
    setCurrentQuestionNum((prev) => {
      if (prev === questionsList.length - 1) return prev;
      else return prev + 1;
    });
  };

  const loadPreviousQuestions = () => {
    axios
      .get("/questions/previous-with-answers")
      .then((res) => {
        setPreviousQuestions(res.data.questions);
        setShowPrevious(true);
      })
      .catch((err) => {
        console.log(err);
        toast.error("Failed to load previous questions");
      });
  };

  const revealAnswer = () => {
    if (!currentQuestion?._id) {
      toast.error("No question available");
      return;
    }
    
    axios
      .get(`/questions/${currentQuestion._id}/reveal-answer`)
      .then((res) => {
        setRevealedAnswer({
          statement: "Correct answer/s to this question: ",
          answer: res.data.correctOptions,
        });
      })
      .catch((err) => {
        console.log(err);
        toast.error(
          err?.response?.data?.msg ||
            "Something went wrong! Please try again later"
        );
      });
  };

  const submitAnswer = () => {
    if (!questionsList[currentQuestionNum] || !currentQuestion?._id) {
      toast.error("No question available");
      return;
    }
    
    const selectedOption = questionsList[currentQuestionNum].tempSelectedOption;

    if (isEmpty(selectedOption) && selectedOption !== 0) {
      toast.error("Please select an option");
      return;
    }
    axios
      .post(`/questions/${currentQuestion._id}/submit-answer`, {
        option: selectedOption,
      })
      .then((res) => {
        dispatch(
          updateAnswers({
            answers: res.data.answers,
            submissionTimestamps: res.data.submissionTimestamps,
          })
        );
        toast.success(res.data.msg);
      })
      .catch((err) => {
        console.log(err);
        toast.error(
          err?.response?.data?.msg ||
            "Something went wrong! Please try again later"
        );
      });
  };

  useEffect(() => {
    axios
      .get("/questions/questions-till-day")
      .then((res) => {
        setQuestionsList(res.data.questions);
        
        if (res.data.questions.length === 0) {
          setQuestionsList([{ empty: true }]);
          setCurrentQuestionNum(0);
          return;
        }
        
        const currentQuestion =
          res.data.questions.length > 0 ? res.data.questions.length - 1 : 0;
        let d1 = new Date(res.data.questions[currentQuestion].releaseDate);
        let d2 = new Date();
        if (
          d1.getDate() === d2.getDate() &&
          d1.getMonth() === d2.getMonth() &&
          d1.getFullYear() === d2.getFullYear()
        )
          setCurrentQuestionNum(currentQuestion);
        else {
          setQuestionsList((prev) => [...prev, { empty: true }]);
          setCurrentQuestionNum(res.data.questions.length);
        }

        if (res.data.lastQuestion) {
          const correctOptions = [];

          res.data.lastQuestion.options.forEach((el) =>
            el.isCorrect
              ? correctOptions.push(
                  el.arabic ? `${el.english} (${el.arabic})` : el.english
                )
              : null
          );
          setRevealedAnswer({
            statement: "Correct/s answer to the yesterday's question: ",
            answer: correctOptions,
          });
        }
      })
      .catch((err) => {
        console.log(err);
        toast.error("Failed to get quiz questions. Please try again later");
      });
  }, []);

  useEffect(() => {
    setCurrentQuestion(questionsList[currentQuestionNum]);
  }, [questionsList, currentQuestionNum]);

  return (
    <>
      <div className="fixed inset-0 -z-10 bg-cover bg-center" style={{ backgroundImage: "url('/assets/imgs/bg.jpg')" }}></div>
      <div
        className={clsx(
          "fixed bg-black/60 backdrop-blur-sm h-screen w-screen left-0 top-0 z-50 transition-opacity",
          revealedAnswer ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={() => setRevealedAnswer(null)}
      ></div>
      <div
        className={clsx(
          "fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 px-6 sm:px-12 md:px-20 py-8 sm:py-10 rounded-2xl bg-white/95 backdrop-blur-2xl border border-white/40 z-50 text-lg sm:text-xl md:text-2xl text-center max-w-[650px] w-[calc(100vw-32px)] shadow-2xl transition-all",
          revealedAnswer ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"
        )}
      >
        <div
          className="absolute right-4 top-2 text-4xl sm:text-5xl cursor-pointer text-slate-400 hover:text-slate-700 transition-colors"
          onClick={() => setRevealedAnswer(null)}
        >
          &times;
        </div>
        <div className="text-slate-800">
          {revealedAnswer?.statement} <br /> <br />{" "}
          <strong className="text-emerald-700">{revealedAnswer?.answer?.join(", ")}</strong>
        </div>
      </div>
      <div className="relative min-h-screen flex flex-col">
        {/* Mobile-first navigation - only show for admin or when navigating */}
        {role === "admin" && (
          <div className="fixed left-0 top-0 w-full pl-4 sm:pl-5 flex justify-start gap-3 sm:gap-4 h-14 sm:h-16 z-20">
            <img
              className="cursor-pointer hover:scale-105 transition-transform w-8 sm:w-10 md:w-12 rotate-90"
              src="/assets/imgs/arrow.svg"
              alt="arrow"
              onClick={prevClickHandler}
            />
            <img
              className="cursor-pointer hover:scale-105 transition-transform w-8 sm:w-10 md:w-12 -rotate-90"
              src="/assets/imgs/arrow.svg"
              alt="arrow"
              onClick={nextClickHandler}
            />
          </div>
        )}

        <div className="flex-1 flex items-center justify-center px-2 sm:px-4 md:px-0 py-12 sm:py-16 md:py-20">
          <div className="w-full max-w-4xl">
            {!currentQuestion?.empty && currentQuestion?._id && (
              <div className="text-center mb-4 md:mb-6 text-xs sm:text-sm md:text-base text-slate-500 font-medium">
                {new Date(currentQuestion?.releaseDate).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
            )}
            
            <div className="relative">
              <div className="text-lg sm:text-xl md:text-2xl lg:text-4xl text-center font-bold px-1 sm:px-2">
                {!currentQuestion?.empty ? (
                  <>
                    <div className="mb-4 md:mb-6 relative leading-tight text-slate-900">
                      {currentQuestion?.english ||
                        "" ||
                        "What is the name of first month according to the Islamic calendar?"}
                    </div>

                    {currentQuestion?.arabic && (
                      <div className="font-noto text-base sm:text-lg md:text-xl lg:text-3xl text-slate-600">
                        ({currentQuestion?.arabic})
                      </div>
                    )}

                    {currentQuestion?.image && (
                      <div className="mt-4 md:mt-6 mb-4 md:mb-6">
                        <img
                          src={`${REACT_APP_BACKEND_URI.replace('/api', '')}${currentQuestion.image}`}
                          alt="Question"
                          className="max-w-full max-h-48 sm:max-h-64 md:max-h-96 mx-auto rounded-2xl shadow-lg"
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-slate-400 text-base sm:text-lg md:text-xl">No Question For Today</div>
                )}
              </div>

            

                  {/* Options */}
                  {!currentQuestion?.empty && (
                  <div className="mt-5 sm:mt-6 md:mt-8">
                    <h3 className="text-base sm:text-lg md:text-xl font-semibold mb-3 sm:mb-4 text-slate-600 text-center">Select Your Answer:</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {currentQuestion?.options?.map((el, idx) => {
                        const isAnswered = currentQuestion?._id && !isEmpty(answers[currentQuestion._id]);
                        const isSelected = questionsList[currentQuestionNum]?.tempSelectedOption === idx ||
                                          (currentQuestion?._id && answers[currentQuestion._id] === idx);
                        
                        return (
                          <button
                            key={"question-option-" + idx}
                            className={clsx(
                              "min-h-14 sm:min-h-16 md:min-h-20 text-sm sm:text-base md:text-lg lg:text-xl font-semibold rounded-xl transition-all py-3 sm:py-4 px-3 sm:px-4 relative border-2",
                              isSelected
                                ? "border-emerald-400 bg-emerald-50 text-emerald-800 shadow-md"
                                : "border-slate-200 bg-white/80 backdrop-blur-xl hover:bg-emerald-50/50 hover:border-emerald-200",
                              isAnswered && "opacity-50 pointer-events-none select-none"
                            )}
                            onClick={() =>
                              setQuestionsList((prev) => {
                                const newList = JSON.parse(
                                  JSON.stringify(prev)
                                );
                                newList[currentQuestionNum].tempSelectedOption = idx;
                                return newList;
                              })
                            }
                          >
                            <div className="absolute top-2 left-2 bg-emerald-600 text-white rounded-full w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center text-xs sm:text-sm font-bold">
                              {String.fromCharCode(65 + idx)}
                            </div>
                            <div className="break-words pt-4 text-slate-800">{el.english}</div>
                            {el.arabic && (
                              <div className="mt-1 sm:mt-2 text-xs sm:text-sm md:text-base break-words text-slate-500">
                                {el.arabic}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  )}
  {!currentQuestion?.empty && (
                <>
                  {/* Countdown Timer */}
                  {currentQuestion?._id && !isEmpty(answers[currentQuestion._id]) && timeRemaining > 0 && (
                    <div className="mt-5 sm:mt-6 mb-5 sm:mb-6 bg-amber-50/90 backdrop-blur-xl border-2 border-amber-200 rounded-2xl p-4 sm:p-5 md:p-6">
                      <div className="text-center">
                        <div className="text-xs sm:text-sm md:text-base text-amber-800 mb-2 font-medium">
                          Answer will be revealed in:
                        </div>
                        <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-amber-900 font-mono tracking-wider">
                          {formatTime(timeRemaining)}
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Action Buttons */}
                  <div className="mt-5 sm:mt-6 md:mt-10 flex flex-col sm:flex-row justify-center sm:justify-end items-stretch sm:items-center gap-3 sm:gap-4">
                    {currentQuestion?._id && !isEmpty(answers[currentQuestion._id]) && (timeRemaining !== null && timeRemaining <= 0) && (
                      <button
                        onClick={revealAnswer}
                        className="w-full sm:w-auto px-5 sm:px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-semibold flex items-center justify-center gap-2 text-sm sm:text-base transition-all shadow-md hover:shadow-lg"
                      >
                        <img src="/assets/imgs/info.svg" alt="info" className="w-4 h-4 sm:w-5 sm:h-5 invert" />
                        View Answer
                      </button>
                    )}
                    
                    {currentQuestion?._id && isEmpty(answers[currentQuestion._id]) && (
                      <Button 
                        onClick={submitAnswer}
                        className="w-full sm:w-auto"
                      >
                        Submit Answer
                      </Button>
                    )}

                    {currentQuestion?._id && !isEmpty(answers[currentQuestion._id]) && role !== "admin" && (
                      <button
                        onClick={loadPreviousQuestions}
                        className="w-full sm:w-auto px-5 sm:px-6 py-3 bg-slate-700 text-white rounded-xl hover:bg-slate-800 font-semibold text-sm sm:text-base transition-all shadow-md hover:shadow-lg"
                      >
                        View Previous Questions
                      </button>
                    )}
                  </div>
                </>
              )}

              {/* Show View Previous Questions button even when there's no question for today */}
              {currentQuestion?.empty && role !== "admin" && (
                <div className="mt-5 sm:mt-6 md:mt-10 flex justify-center">
                  <button
                    onClick={loadPreviousQuestions}
                    className="w-full sm:w-auto px-5 sm:px-6 py-3 bg-slate-700 text-white rounded-xl hover:bg-slate-800 font-semibold text-sm sm:text-base transition-all shadow-md hover:shadow-lg"
                  >
                    View Previous Questions
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Previous Questions Modal */}
        {showPrevious && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4">
            <div className="bg-white/95 backdrop-blur-2xl border border-white/40 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="sticky top-0 bg-white/95 backdrop-blur-xl border-b border-slate-100 px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center rounded-t-2xl">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-slate-900">Previous Questions</h2>
                <button
                  onClick={() => setShowPrevious(false)}
                  className="text-2xl sm:text-3xl text-slate-400 hover:text-slate-700 transition-colors w-10 h-10 flex items-center justify-center rounded-xl hover:bg-slate-100"
                >
                  &times;
                </button>
              </div>
              <div className="p-4 sm:p-6 space-y-5 sm:space-y-6">
                {previousQuestions.slice().reverse().map((q, qIdx) => {
                  const userAnswer = answers[q._id];
                  const selectedOption = q.options[userAnswer];
                  const isCorrect = selectedOption?.isCorrect;
                  
                  return (
                    <div key={q._id} className="border border-slate-200 rounded-xl p-4 sm:p-6 bg-white/60">
                      <div className="flex justify-between items-start mb-2">
                        <div className="text-xs sm:text-sm font-semibold text-emerald-600">
                          Question {previousQuestions.length - qIdx}
                        </div>
                        <div className="text-xs sm:text-sm text-slate-500">
                          {new Date(q.releaseDate).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="font-bold text-base sm:text-lg md:text-xl mb-3 text-slate-900">
                        {q.english}
                      </div>
                      {q.arabic && (
                        <div className="text-slate-600 mb-3 font-noto text-sm sm:text-base">
                          {q.arabic}
                        </div>
                      )}
                      {q.image && (
                        <img
                          src={`${REACT_APP_BACKEND_URI.replace('/api', '')}${q.image}`}
                          alt="Question"
                          className="max-w-full max-h-36 sm:max-h-48 mx-auto rounded-xl mb-4"
                        />
                      )}
                      <div className="mb-3">
                        <div className="font-semibold text-slate-600 mb-2 text-sm sm:text-base">All Options:</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {q.options.map((opt, optIdx) => (
                            <div 
                              key={optIdx}
                              className={`p-2 sm:p-3 rounded-xl border-2 ${
                                opt.isCorrect 
                                  ? 'bg-emerald-50 border-emerald-300' 
                                  : optIdx === userAnswer 
                                    ? 'bg-red-50 border-red-300'
                                    : 'bg-slate-50 border-slate-200'
                              }`}
                            >
                              <div className="flex items-start gap-2">
                                <div className="bg-emerald-600 text-white rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                  {String.fromCharCode(65 + optIdx)}
                                </div>
                                <div className="text-xs sm:text-sm flex-1 text-slate-800">
                                  {opt.english}
                                  {opt.arabic && <div className="text-xs mt-1 text-slate-500">{opt.arabic}</div>}
                                  {opt.isCorrect && <span className="ml-1 sm:ml-2 text-emerald-600 font-bold">&#10003; Correct</span>}
                                  {optIdx === userAnswer && !opt.isCorrect && <span className="ml-1 sm:ml-2 text-red-600 font-bold">&#10007; Your Answer</span>}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className={`p-3 rounded-xl ${isCorrect ? 'bg-emerald-100 border border-emerald-300' : 'bg-red-100 border border-red-300'}`}>
                        <div className="font-semibold text-sm sm:text-base">
                          Result: {isCorrect ? '\u2713 Correct' : '\u2717 Incorrect'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default QuizQuestion;
